/// <amd-module name="Test/common-converters/TestResultConverter" />
import { TestCaseResult, TestRun, ResultGroupType } from "Test/common-util/Contracts/Test";
import { IViewContextData, ViewContext, TestOutcomeEnum } from "Test/common-util/Common";
import { ITestResultTreeData, ITestDetailsPanePayload, TreeNodeType } from "Test/common-util/Contracts/ViewContracts";
import { TCMContractsConverter } from "Test/common-util/Common.Utils";
import { ago } from "VSSUI/Utilities/Date";
import * as Resources from "Test/common-converters/Resources";
import { TestCaseResultIdentifierWithDuration } from "Test/common-util/Common";

export function getResultViewModelFromTestResult(testCaseResult: TestCaseResult, viewContextData: IViewContextData, groupId: string): ITestResultTreeData {
    let contextId: number = 0;
    let contextName: string = "";
    let context = viewContextData;
    let isCurrentArtifact: boolean = false;
    if (testCaseResult.failingSince && context) {
        switch (context.viewContext) {
            case ViewContext.Build:
                contextId = testCaseResult.failingSince.build ? testCaseResult.failingSince.build.id : 0;
                contextName = getFailingSinceBuildString(context.data.mainData.id, testCaseResult);
                isCurrentArtifact = context.data.mainData.id === testCaseResult.failingSince.build.id;
                break;
            case ViewContext.Release:
                contextId = testCaseResult.failingSince.release ? testCaseResult.failingSince.release.id : 0;
                contextName = getFailingSinceReleaseString(context.data.mainData.id, testCaseResult);
                isCurrentArtifact = context.data.mainData.id === testCaseResult.failingSince.release.id;
                break;
        }
    }

    const resultViewModel: ITestResultTreeData = {
        test: testCaseResult.testCaseTitle,
        testTitle: testCaseResult.automatedTestName,
        storage: testCaseResult.automatedTestStorage || "",
        failingSince: testCaseResult.failingSince ? ago(testCaseResult.failingSince.date) : "",
        failingContextName: contextName,
        isNewFailure: testCaseResult.failingSince ? context.data.mainData.id === testCaseResult.failingSince.build.id : false,
        runId: parseInt(testCaseResult.testRun.id),
        resultId: testCaseResult.id,
        testcaseObject: testCaseResult.testCase,
        testCaseRefId: testCaseResult.testCaseReferenceId,
        failingContextId: contextId,
        isTestCaseRow: true,
        expanded: false,
        outcome: (<any>TestOutcomeEnum)[testCaseResult.outcome],
        duration: (testCaseResult.durationInMs) ? TCMContractsConverter.ConvertMilliSecondsToReadableFormatForResultSummary(testCaseResult.durationInMs) : TCMContractsConverter.ConvertMilliSecondsToReadableFormatForResultSummary(0),
        durationInMs: (testCaseResult.durationInMs) ? testCaseResult.durationInMs : 0,
        dateStarted: (testCaseResult.startedDate) ? testCaseResult.startedDate.toLocaleString() : "",
        dateCompleted: (testCaseResult.completedDate) ? testCaseResult.completedDate.toLocaleString() : "",
        owner: (testCaseResult.owner) ? testCaseResult.owner.displayName : "",
        parentGroupId: groupId,
        subresults: testCaseResult.subResults,
        groupId: testCaseResult.testRun.id.toString() + "." + testCaseResult.id.toString(),
        nodeType: testCaseResult.resultGroupType != null && testCaseResult.resultGroupType !== ResultGroupType.None ? TreeNodeType.group : TreeNodeType.leaf,
        environmentName: testCaseResult.releaseReference ? getEnvironmentName(context) : "",
        isSubResultRow: false,
        isCurrentArtifact: isCurrentArtifact,
        // TODO: TagsWork
        // This is the place we have to update model with tag info from testCaseResult
        //tags: testCaseResult.tags,
    };

    return resultViewModel;
}

export function getRunViewModelFromTestRun(testRun: TestRun, viewContextData: IViewContextData): ITestResultTreeData {
    let context = viewContextData;

    const runViewModel: ITestResultTreeData = {
        test: testRun.name,
        isTestCaseRow: false,
        duration: "-", // Getting duration using group api is extra call, hence not giving for full view.
        testTitle: testRun.name,
        runId: testRun.id,
        expanded: false,
        parentGroupId: "-1",
        groupId: "",
        nodeType: TreeNodeType.group,
    };

    return runViewModel;
}


export function getFailingSinceBuildString(buildId: number, result: TestCaseResult): string {
    const contextName: string = (buildId === result.failingSince.build.id) ? Resources.CurrentBuild : result.failingSince.build.number;
    return contextName;
}

export function getFailingSinceReleaseString(releaseId: number, result: TestCaseResult): string {
    const contextName: string = (releaseId === result.failingSince.release.id) ? Resources.CurrentRelease : result.failingSince.release.name;
    return contextName;
}

export function getEnvironmentName(context: IViewContextData): string {
    let environmentName = "";
    if (context && context.data.subData) {
        environmentName = context.data.subData.environment.name;
    }
    return environmentName;
}

export function sortDuration(durationFirst: number, durationSecond: number, isAscending: boolean){
    durationFirst = durationFirst ? durationFirst : 0;
    durationSecond = durationSecond ? durationSecond : 0;
    if (isAscending) {
        return durationFirst - durationSecond;
    }
    return durationSecond - durationFirst;
}

export function sortingIdentifierAscending(a: TestCaseResultIdentifierWithDuration, b: TestCaseResultIdentifierWithDuration) {
    return sortDuration(a.durationInMs, b.durationInMs, true);
}

export function sortingIdentifierDescending(a: TestCaseResultIdentifierWithDuration, b: TestCaseResultIdentifierWithDuration) {
    return sortDuration(a.durationInMs, b.durationInMs, false);
}

export function sortingComparatorAscending(a: TestCaseResult, b: TestCaseResult) {
    return sortDuration(a.durationInMs, b.durationInMs, true);
}

export function sortingComparatorDescending(a: TestCaseResult, b: TestCaseResult) {
    return sortDuration(a.durationInMs, b.durationInMs, false);
}

export function sortingTestTreeDataComparerDescending(a: ITestResultTreeData, b: ITestResultTreeData) {
    return sortDuration(a.durationInMs ? a.durationInMs : 0, b.durationInMs ? b.durationInMs : 0, false);
}

export function sortingTestTreeDataComparerAscending(a: ITestResultTreeData, b: ITestResultTreeData) {
    return sortDuration(a.durationInMs ? a.durationInMs : 0, b.durationInMs ? b.durationInMs : 0, true);
}